import type {
  PowerTransformerObject,
  LoadFlowScenarioResults,
  LoadFlowScenarioElementCalculation
} from '../../../..';
import type {
  PowerTransformerEvaluatedAvailableCapacities,
  PowerTransformerEvaluatedLoadingPercents
} from '../../../../derived-state/loadFlowSummary';
import { Percent } from '../../../../domain/Percent';
import { RatedCapacity } from '../../../../domain/ratedCapacity';
import type { ForStoringAttentionStatus } from '../../../AttentionStatusRepository';
import {
  selectByType,
  selectByTypeFromAllScenarios
} from '../../../selectByType';
import type { ForPowerTransformerEvaluation } from './forPowerTransformerEvaluation';
import type { HighestLoadingPowerTransformerEvaluationResult } from './highestLoadingPowerTransformerEvaluation';
import { PowerWithHighestLoadingEvaluation } from './highestLoadingPowerTransformerEvaluation';
import { PowerTransformerSummaryEvaluation } from './powerTransformerSummaryEvaluation';

export class PowerTransformerEvaluation {
  private powerTransformers: PowerTransformerObject[];
  private evaluationStatusRepository: ForStoringAttentionStatus;

  private constructor(
    PowerTransformers: PowerTransformerObject[],
    evaluationStatusRepository: ForStoringAttentionStatus
  ) {
    this.powerTransformers = PowerTransformers;
    this.evaluationStatusRepository = evaluationStatusRepository;
  }

  static from(
    loadFlowScenarios: LoadFlowScenarioResults[],
    evaluationEventHandler: ForStoringAttentionStatus
  ) {
    const results = selectByTypeFromAllScenarios(
      'PowerTransformer',
      loadFlowScenarios
    );
    return new PowerTransformerEvaluation(results, evaluationEventHandler);
  }

  private get powerTransformerResults() {
    return this.powerTransformers.map(
      PowerTransformer => PowerTransformer.result
    );
  }

  static fromResults(
    loadFlowResults: LoadFlowScenarioElementCalculation[],
    evaluationEventHandler: ForStoringAttentionStatus
  ) {
    const results = selectByType('PowerTransformer', loadFlowResults).map(
      value => value
    );
    return new PowerTransformerEvaluation(results, evaluationEventHandler);
  }

  evaluateSummary(
    evaluationEngine: ForPowerTransformerEvaluation
  ): PowerTransformerEvaluationResult {
    const summaryEvaluation = new PowerTransformerSummaryEvaluation(
      this.powerTransformerResults,
      evaluationEngine
    );

    const loadingPercentResult = summaryEvaluation.evaluateLoadingPercents();
    const availableCapacityResult =
      summaryEvaluation.evaluateAvailableCapacities();
    return {
      loadingPercent: loadingPercentResult,
      availableCapacity: availableCapacityResult
    };
  }

  evaluateEachPowerTransformer(
    evaluationEngine: ForPowerTransformerEvaluation
  ) {
    return this.powerTransformers.map(powerTransformer => {
      const messenger = this.evaluationStatusRepository.delegateMessenger(
        powerTransformer.id,
        powerTransformer.objectType
      );

      const loadingPercent = evaluationEngine.evaluateLoadingPercent(
        Percent.fromFloat(powerTransformer.result.loadingPercent),
        messenger
      );
      const availableCapacity = evaluationEngine.evaluateAvailableCapacity(
        powerTransformer.result.availableCapacity,
        messenger
      );
      const ratedCapacity = evaluationEngine.evaluateRatedCapacity(
        RatedCapacity.calculate(
          powerTransformer.result.availableCapacity,
          powerTransformer.result.loadingPercent
        ).value,
        messenger
      );

      return {
        id: powerTransformer.id,
        objectType: powerTransformer.objectType,
        results: {
          loadingPercent,
          availableCapacity,
          ratedCapacity
        }
      };
    });
  }

  evaluateOneWithHighestLoadingPercent(
    evaluationEngine: ForPowerTransformerEvaluation
  ): HighestLoadingPowerTransformerEvaluationResult {
    return new PowerWithHighestLoadingEvaluation(
      this.powerTransformers,
      evaluationEngine,
      this.evaluationStatusRepository
    ).evaluate();
  }
}

export type PowerTransformerEvaluationResult = {
  loadingPercent: PowerTransformerEvaluatedLoadingPercents;
  availableCapacity: PowerTransformerEvaluatedAvailableCapacities;
};
